import { useCallback, useSyncExternalStore } from 'react';

/**
 * A reading preference that survives a reload.
 *
 * `localStorage` is explicitly allowed for theme and reading preferences, and
 * explicitly forbidden for tokens — this holds the former only. Every access
 * is wrapped: storage throws outright in some contexts (a private window with
 * site data blocked, for instance), and a reader losing the whole screen
 * because their browser refused to remember a layout choice would be absurd.
 */
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  // Another tab changing it should not leave this one disagreeing.
  window.addEventListener('storage', onChange);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onChange);
  };
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function useStoredPreference<T extends string>(
  key: string,
  fallback: T,
  isValid: (value: unknown) => value is T,
): [T, (value: T) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => {
      const stored = read(key);
      // A stored value from an older version may no longer be valid; falling
      // back beats rendering a mode that no longer exists.
      return isValid(stored) ? stored : fallback;
    },
    () => fallback,
  );

  const setValue = useCallback(
    (next: T) => {
      try {
        window.localStorage.setItem(key, next);
      } catch {
        // Not remembering is a smaller failure than not working.
      }
      notify();
    },
    [key],
  );

  return [value, setValue];
}
